/* eslint-disable max-len */
import { IconButton, Radio } from "@mui/material";
import { MastercardIcon, TrashIcon, VisaIcon } from "assets/Icons";
import { useEffect, useState } from "react";
import { muiColors } from "themes/colors";
import * as Axios from "providers/axios";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslate, useNotify } from "react-admin";
import ConfirmationModal from "./ConfirmationModal";

const CardBrandIcon = ({ brand }: { brand: string }) => {
  if (brand === "mastercard") return <MastercardIcon />;

  return <VisaIcon />;
};
interface ShowCardsProps {
  user?: any;
  company?: any;
  editMode?: boolean;
  refetch: () => Promise<any>;
  selectedDefaultCardIdState?: string;
  setSelectedDefaultCardIdState?: React.Dispatch<React.SetStateAction<string>>;
}

const ShowCards = ({
  user,
  company,
  editMode,
  selectedDefaultCardIdState,
  refetch,
  setSelectedDefaultCardIdState
}: ShowCardsProps) => {
  const [conformDeleteCardId, setConformDeleteCardId] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const { api } = Axios.useAxios();
  const navigate = useNavigate();
  const translate = useTranslate();
  const notify = useNotify();
  const userOrCompany = user || company;

  useEffect(() => {
    const defaultCard = userOrCompany?.StripePaymentMethods?.find(
      (card: any) => card.is_default
    );
    if (defaultCard && setSelectedDefaultCardIdState) {
      setSelectedDefaultCardIdState(defaultCard.id);
    }
    refetch();
  }, [
    userOrCompany?.StripePaymentMethods,
    setSelectedDefaultCardIdState,
    refetch
  ]);

  const form = useFormContext();

  const handleDeleteCard = async () => {
    try {
      setIsDeleting(true);
      await api.delete(`/v1/payment-methods/${conformDeleteCardId}`);
      setConformDeleteCardId("");
      await refetch();
      if (company) {
        navigate(`/dashboard/Company/${company?.id}/show`);
        return;
      }
      navigate(`/dashboard/Customer/${user?.id}/show`);
    } catch (e: any) {
      setConformDeleteCardId("");
      const message =
        e?.response?.data?.message || e?.message || translate("common.error");
      notify(message, { type: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="w-full">
      {userOrCompany?.StripePaymentMethods?.length > 0 && (
        <div
          className={`text-[14px] font-['Inter'] font-[500] ${
            editMode ? "mt-4 mb-2" : "text-[rgba(0,0,0,0.6)]"
          }`}
        >
          {translate("users.saved_credit_cards")}:
        </div>
      )}
      <div>
        {userOrCompany?.StripePaymentMethods?.map(
          (card: any, index: number) => (
            <div className="flex items-center gap-2" key={card.id}>
              <div
                key={card.id}
                onClick={() => {
                  if (setSelectedDefaultCardIdState) {
                    setSelectedDefaultCardIdState(card.id);
                    form.setValue("fakeDirtyFormWorkaround", "true", {
                      shouldDirty: true
                    });
                  }
                }}
                aria-hidden
                className={`w-full flex justify-between items-center px-4 py-[6px] ${
                  editMode ? "cursor-pointer" : "cursor-default"
                } border-[rgba(48,47,46,0.15)] ${
                  index === 0 ? "border-none" : "border-t"
                }`}
              >
                <div className="flex items-center gap-[16px]">
                  {card?.brand && <CardBrandIcon brand={card?.brand} />}
                  <div>
                    <div className="text-[14px] font-['Inter'] font-[400] text-mono-primaryTextColor capitalize">
                      {card?.brand} * {card?.last4}{" "}
                      {card?.is_default ? "(default)" : ""}
                    </div>
                    <div className="text-[14px] font-['Inter'] font-[400] text-mono-secondaryTextColor">
                      {card?.exp_month < 10
                        ? `0${card?.exp_month}`
                        : card?.exp_month}
                      /{card?.exp_year?.toString()?.slice(2, 4)}
                    </div>
                  </div>
                </div>
                {editMode && (
                  <Radio
                    checked={selectedDefaultCardIdState === card.id}
                    sx={{
                      "&.Mui-checked": {
                        color: muiColors.primary
                      }
                    }}
                  />
                )}
              </div>
              {editMode && (
                <IconButton
                  onClick={() => setConformDeleteCardId(card.id)}
                  sx={{ width: "48px", height: "48px" }}
                >
                  <TrashIcon />
                </IconButton>
              )}
            </div>
          )
        )}
      </div>
      {editMode && (
        <ConfirmationModal
          open={!!conformDeleteCardId}
          handleConfirm={handleDeleteCard}
          onClose={() => setConformDeleteCardId("")}
          text="Are you sure you want to delete? This action can't be reversed, so please be certain before proceeding."
          buttonText="Delete"
          disabled={isDeleting}
        />
      )}
    </div>
  );
};

export default ShowCards;
